import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as ReadyIcon,
  Warning as DuplicateIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  CloudUpload as ImportIcon,
} from '@mui/icons-material';
import CompanySecretaryDuplicateResolutionPanel from './CompanySecretaryDuplicateResolutionPanel';

const STATUS_CONFIG = {
  ready: { icon: ReadyIcon, label: 'Ready', chipColor: 'success' },
  duplicate: { icon: DuplicateIcon, label: 'Duplicate', chipColor: 'warning' },
  error: { icon: ErrorIcon, label: 'Error', chipColor: 'error' },
};

const CompanySecretaryImportReviewModal = ({ open, onClose, importedSecretaries, onImport }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSecretary, setSelectedSecretary] = useState(null);
  const [secretaries, setSecretaries] = useState(importedSecretaries);

  useEffect(() => {
    setSecretaries(importedSecretaries);
  }, [importedSecretaries]);

  const summary = useMemo(() => ({
    total: secretaries.length,
    ready: secretaries.filter((s) => s.status === 'ready').length,
    duplicates: secretaries.filter((s) => s.status === 'duplicate').length,
    errors: secretaries.filter((s) => s.status === 'error').length,
  }), [secretaries]);

  const filteredSecretaries = useMemo(() => {
    let result = secretaries;
    if (filter !== 'all') result = result.filter((s) => s.status === filter);
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter((s) =>
        s.firstName?.toLowerCase().includes(search)
        || s.lastName?.toLowerCase().includes(search)
        || s.email?.toLowerCase().includes(search));
    }
    return result;
  }, [secretaries, filter, searchTerm]);

  const handleResolutionComplete = (resolution) => {
    setSecretaries((prev) =>
      prev.map((s) =>
        s._rowIndex === resolution.secretaryIndex
          ? { ...s, status: 'ready', resolution }
          : s));
    setSelectedSecretary(null);
  };

  const issueCount = summary.duplicates + summary.errors;
  const canImportAll = issueCount === 0;

  return (
    <>
      <Dialog open={open && !selectedSecretary} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Company Secretary Import Review</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
            <Box display="flex" gap={3} justifyContent="center">
              {['total', 'ready', 'duplicates', 'errors'].map((key) => (
                <Box key={key} textAlign="center">
                  <Typography variant="h4" color={key === 'ready' ? 'success.main' : key === 'errors' ? 'error.main' : key === 'duplicates' ? 'warning.main' : 'primary'}>
                    {summary[key]}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          <Box display="flex" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter</InputLabel>
              <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Filter">
                <MenuItem value="all">All ({summary.total})</MenuItem>
                <MenuItem value="ready">Ready ({summary.ready})</MenuItem>
                <MenuItem value="duplicate">Duplicates ({summary.duplicates})</MenuItem>
                <MenuItem value="error">Errors ({summary.errors})</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="50">#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Assigned Companies</TableCell>
                  <TableCell width="150">Status</TableCell>
                  <TableCell width="100" align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSecretaries.map((secretary) => {
                  const statusConfig = STATUS_CONFIG[secretary.status];
                  const StatusIcon = statusConfig.icon;
                  return (
                    <TableRow key={secretary._rowIndex} hover>
                      <TableCell>{secretary._rowIndex + 1}</TableCell>
                      <TableCell>{`${secretary.firstName} ${secretary.lastName}`.trim()}</TableCell>
                      <TableCell>{secretary.email}</TableCell>
                      <TableCell>
                        {secretary.assignedCompanyNames?.length
                          ? secretary.assignedCompanyNames.join(', ')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip icon={<StatusIcon />} label={statusConfig.label} color={statusConfig.chipColor} size="small" />
                        {secretary.errors?.length > 0 && (
                          <Tooltip title={secretary.errors.join(', ')}>
                            <Typography variant="caption" color="error" display="block">{secretary.errors.length} error(s)</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {(secretary.status === 'duplicate' || secretary.status === 'error') && (
                          <Button size="small" variant="outlined" onClick={() => setSelectedSecretary(secretary)}>
                            Resolve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">Cancel Import</Button>
          {summary.ready > 0 && (
            <Button variant="outlined" onClick={() => onImport(secretaries.filter((s) => s.status === 'ready'))} startIcon={<ImportIcon />}>
              Import Ready Only ({summary.ready})
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => onImport(secretaries)}
            disabled={!canImportAll}
            startIcon={<ImportIcon />}
          >
            Import All ({summary.total})
          </Button>
        </DialogActions>
      </Dialog>

      {selectedSecretary && (
        <CompanySecretaryDuplicateResolutionPanel
          open={!!selectedSecretary}
          secretary={selectedSecretary}
          onClose={() => setSelectedSecretary(null)}
          onResolve={handleResolutionComplete}
        />
      )}
    </>
  );
};

export default CompanySecretaryImportReviewModal;
